import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  model,
  output,
} from '@angular/core'

import { SplitAreaComponent, SplitComponent } from 'angular-split'

import { FlxModalModule, FlxTabBarModule } from '@flowx/angular-ui-toolkit'
import { PreviewMode } from '@flowx/designer/modules/script-builder/constants/expressions.constants'
import { ScriptingModalStore } from '@flowx/designer/modules/script-builder/services/scripting-modal.store'

export type TestModalAreaSection = {
  title: string
  subtitle: string
  size?: number
}

export type TestModalAreaSections = {
  leftSection: TestModalAreaSection
  middleSection?: TestModalAreaSection
  rightSection: TestModalAreaSection
}

export type TestModalTab = {
  id: string
  label: string
}

@Component({
  selector: 'flx-test-modal',
  templateUrl: './test-modal.component.html',
  styleUrls: ['./test-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FlxModalModule, FlxTabBarModule, SplitComponent, SplitAreaComponent],
})
export class TestModalComponent implements OnDestroy {
  #scriptingModalStore = inject(ScriptingModalStore)

  title = input.required<string>()
  subtitle = input<string>()
  customHeader = input<boolean>(false)
  sections = input.required<TestModalAreaSections>()
  previewMode = input<PreviewMode>('none')

  tabs = input<TestModalTab[]>([])
  selectedTabIndex = model(0)

  hasMiddleSection = computed(() => !!this.sections().middleSection)
  sectionSizes = computed(() => {
    const { leftSection, middleSection, rightSection } = this.sections()

    const hasCustomSizes = this.hasMiddleSection()
      ? leftSection.size !== undefined &&
        middleSection?.size !== undefined &&
        rightSection.size !== undefined
      : leftSection.size !== undefined && rightSection.size !== undefined

    if (hasCustomSizes) {
      return {
        leftSection: leftSection.size,
        middleSection: middleSection?.size ?? 0,
        rightSection: rightSection.size,
      }
    }

    if (!this.hasMiddleSection()) {
      return { leftSection: 50, middleSection: 0, rightSection: 50 }
    }

    switch (this.previewMode()) {
      case 'side-by-side':
        return { leftSection: 15, middleSection: 70, rightSection: 15 }
      case 'center-aligned':
        return { leftSection: 20, middleSection: 65, rightSection: 15 }
      default:
        return { leftSection: 33, middleSection: 33, rightSection: 33 }
    }
  })

  testModalClose = output()

  closeModal(): void {
    this.testModalClose.emit()
  }

  ngOnDestroy(): void {
    this.#scriptingModalStore.activeModal.set(null)
  }
}
